"use client";

import { type CSSProperties, JSX, useEffect, useMemo, useRef } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { scrollIntoViewX } from "@/lib/scroll-into-view-x";
import { SvgFadeMask } from "@/components/charts/svg-fade-mask";
import { useSvgId } from "@/lib/hooks/use-svg-id";
import type { ProcessStatus } from "@/components/charts/process-status";
import {
  useHoverSelect,
  useLegendHoverSelect,
  useSelectionGroup,
} from "@/lib/hooks/use-hover-select";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { NodeTooltip } from "@/components/charts/tooltip/node-tooltip";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import {
  useTextMetrics,
  type TextMetricsItem,
} from "./shared/use-text-metrics";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";
import {
  legendIndexOfCategory,
  resolveCategoryColor,
} from "./shared/category-resolve";
import type { ModuleCategory } from "@/types/modules";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { accentToneOr, seriesTokenStyle } from "./module-accent";

export type ProgressStagesStage = {
  id: string;
  label: string;
  detail?: string;
  status?: ProcessStatus;
  categoryId?: string;
};

export type ProgressStagesData = {
  stages: ProgressStagesStage[];
  categories?: ModuleCategory[];
};

const hasProgressStagesGrouping = (
  stages: ProgressStagesStage[],
  categories?: ModuleCategory[],
) =>
  Boolean(categories && categories.length > 0) ||
  stages.some((s) => s.status !== undefined || s.categoryId !== undefined);

const DEFAULT_DATA: ProgressStagesData = {
  stages: [
    { id: "commit", label: "Commit", detail: "24 jobs", status: "done" },
    { id: "build", label: "Build", detail: "avg 3m 12s", status: "done" },
    { id: "test", label: "Test", detail: "94% pass", status: "done" },
    { id: "staging", label: "Staging", detail: "smoke ok", status: "active" },
    { id: "canary", label: "Canary", detail: "10% shift", status: "pending" },
    { id: "prod", label: "Prod", detail: "—", status: "pending" },
  ],
};

const CHEV_MIN_W = 112;
const CHEV_MAX_W = 220;
const CHEV_GAP = 12;
const CHEV_H = 56;
const CHEV_CHAR_W = 7;
const CHEV_LABEL_PAD = 52;

const labelTextStyle: CSSProperties = {
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: 1.15,
  textAlign: "center",
  overflowWrap: "anywhere",
};

/**
 * First paint only, until the hidden measuring layer reports the rendered ink
 * width. A flat per-character width is wrong in both directions — spaces and
 * narrow glyphs come out too wide, caps and wide glyphs too narrow — so it may
 * never decide the final layout.
 */
function estimateLabelWidth(item: TextMetricsItem): number {
  return Math.ceil(item.text.length * CHEV_CHAR_W);
}

function chevronWidth(inkWidth: number): number {
  return Math.max(
    CHEV_MIN_W,
    Math.min(CHEV_MAX_W, Math.ceil(inkWidth) + CHEV_LABEL_PAD),
  );
}

export default function ModuleProgressStages({
  data = DEFAULT_DATA,
  showLegend = true,
  grayscale,
  accent,
  className,
}: {
  data?: ProgressStagesData;
  showLegend?: boolean;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const stages = data.stages ?? [];
  const freeCategories = data.categories;
  const grouped = hasProgressStagesGrouping(stages, freeCategories);
  const selection = useSelectionGroup();
  const stage = useHoverSelect<string>({ group: selection });
  const effective = stage.effective;
  const legend = useLegendHoverSelect({ group: selection });
  const hoverGesture = useHoverGesture();
  const maskId = useSvgId("cp-mask");
  const activeTone = accentToneOr(grayscale, accent, "var(--emerald)");

  const startX = 20;
  const startY = 24;

  const labels = useMemo<TextMetricsItem[]>(
    () =>
      stages.map((s) => ({
        id: s.id,
        text: stripInlineMarkdown(s.label),
        style: labelTextStyle,
      })),
    [stages],
  );

  const { layer: labelMeasureLayer, sizes: inkWidths } = useTextMetrics({
    axis: "width",
    items: labels,
    fallback: estimateLabelWidth,
  });

  const chevPositions = useMemo(() => {
    const arr: { ox: number; w: number }[] = [];
    let x = startX;
    labels.forEach(({ id }) => {
      const w = chevronWidth(inkWidths[id]);
      arr.push({ ox: x, w });
      x += w + CHEV_GAP;
    });
    return arr;
  }, [labels, inkWidths]);
  const W =
    chevPositions.length > 0
      ? chevPositions[chevPositions.length - 1].ox +
        chevPositions[chevPositions.length - 1].w +
        startX
      : startX * 2 + CHEV_MIN_W;
  const H = startY + CHEV_H + 60;
  const { svgRef, height: dynamicH } = useSvgContentHeight(H, [stages, W]);

  const legendItems = useMemo(() => {
    if (freeCategories && freeCategories.length > 0) {
      return freeCategories.map((cat) => ({
        id: cat.id,
        label: cat.label,
        color: cat.color,
        value: stages.filter((s) => s.categoryId === cat.id).length,
      }));
    }
    const counts = stages.reduce<Record<string, number>>((acc, s) => {
      if (s.status) acc[s.status] = (acc[s.status] ?? 0) + 1;
      return acc;
    }, {});
    return [
      { id: "done", label: t("Done"), value: counts.done ?? 0, color: "var(--muted-foreground)" },
      { id: "active", label: t("Active"), value: counts.active ?? 0, color: activeTone },
      { id: "pending", label: t("Pending"), value: counts.pending ?? 0, color: "var(--track)" },
    ];
  }, [stages, freeCategories, activeTone, t]);

  const stageGroupKey = (s: ProgressStagesStage): string | undefined =>
    freeCategories && freeCategories.length > 0 ? s.categoryId : s.status;

  const activeStatusFilter =
    legend.filter !== null ? legendItems[legend.filter]?.id ?? null : null;

  const selectedStage = stage.selected
    ? stages.find((s) => s.id === stage.selected)
    : null;
  const legendSelectedIndex =
    legend.selected ??
    legendIndexOfCategory(
      legendItems,
      selectedStage ? stageGroupKey(selectedStage) : null,
    );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const anchorRefs = useRef<Record<string, SVGGElement | null>>({});
  const activeStage = effective
    ? stages.find((s) => s.id === effective) ?? null
    : null;
  const activeKey = activeStage ? stageGroupKey(activeStage) : undefined;
  const activeStatusMeta = activeKey
    ? legendItems.find((li) => li.id === activeKey)
    : null;
  // Both surfaces feed one scroll target: a legend row centres the first stage
  // of its category, a stage picked in the chevron row is only pulled in when
  // it sits outside the viewport. Keying the effect on the resolved index
  // instead of the rebuilt arrays keeps it from firing on unrelated renders,
  // which is what makes re-picking the same category scroll again.
  const scrollCategory =
    legend.selected !== null ? legendItems[legend.selected]?.id ?? null : null;
  const scrollStageIndex = (() => {
    const i =
      scrollCategory !== null
        ? stages.findIndex((s) => stageGroupKey(s) === scrollCategory)
        : stage.selected !== null
          ? stages.findIndex((s) => s.id === stage.selected)
          : -1;
    return i < 0 ? null : i;
  })();
  const scrollAlign = scrollCategory !== null ? "center" : "nearest";
  useEffect(() => {
    if (scrollStageIndex === null) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    scrollIntoViewX(
      container,
      container.querySelector(`[data-part-index="${scrollStageIndex}"]`),
      { align: scrollAlign, once: false },
    );
  }, [scrollStageIndex, scrollAlign]);

  return (
    <div
      className={cn("relative flex w-full flex-col gap-4", className)}
      style={seriesTokenStyle(grayscale, accent)}
    >
      {labelMeasureLayer}

      <SwipeScroll ref={scrollContainerRef} className="relative w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${dynamicH}`}
          className="h-auto w-full"
          style={{ overflow: "visible", minWidth: `${W}px` }}
        >
          <SvgFadeMask id={maskId} />
          <g mask={`url(#${maskId})`}>
            <line
              x1={0}
              x2={W}
              y1={startY + CHEV_H / 2}
              y2={startY + CHEV_H / 2}
              stroke="var(--grid)"
              strokeDasharray="4 4"
              strokeWidth="1"
              fill="none"
              opacity="0.5"
            />
          </g>

          {stages.map((s, i) => {
            const { ox, w: chevW } = chevPositions[i];
            const oy = startY;
            const points = [
              [ox, oy + 0],
              [ox + chevW - 16, oy + 0],
              [ox + chevW, oy + 28],
              [ox + chevW - 16, oy + 56],
              [ox, oy + 56],
              [ox + 16, oy + 28],
            ]
              .map((p) => p.join(","))
              .join(" ");

            const groupKey = stageGroupKey(s);
            const isDone = grouped && !freeCategories && s.status === "done";
            const isActive = grouped && !freeCategories && s.status === "active";
            const isHovered = effective === s.id;
            const legDim =
              grouped &&
              activeStatusFilter !== null &&
              activeStatusFilter !== groupKey;
            const dim = (effective !== null && !isHovered) || legDim;

            let fill: string;
            let stroke: string;
            let textFill: string;
            let detailFill: string;
            if (freeCategories && freeCategories.length > 0 && s.categoryId) {
              const catColor = resolveCategoryColor(s.categoryId, freeCategories, "var(--muted)");
              fill = catColor;
              stroke = "rgba(0,0,0,0.12)";
              textFill = "#ffffff";
              detailFill = "rgba(255,255,255,0.85)";
            } else if (!grouped) {
              fill = "var(--muted)";
              stroke = "var(--border)";
              textFill = "var(--foreground)";
              detailFill = "var(--muted-foreground)";
            } else if (isDone) {
              fill = "var(--muted)";
              stroke = "var(--border)";
              textFill = "var(--foreground)";
              detailFill = "var(--muted-foreground)";
            } else if (isActive) {
              fill = activeTone;
              stroke = "rgba(0,0,0,0.12)";
              textFill = "#ffffff";
              detailFill = "rgba(255,255,255,0.85)";
            } else {
              fill = "var(--track)";
              stroke = "var(--border)";
              textFill = "var(--muted-foreground)";
              detailFill = "var(--subtle)";
            }

            return (
              <g
                key={s.id}
                ref={(el) => {
                  anchorRefs.current[s.id] = el;
                }}
                data-module-type="progress-stages"
                data-part-index={i}
                data-part-label={s.label}
                onPointerEnter={(e) => {
                  if (hoverGesture.enter(e)) stage.setHovered(s.id);
                }}
                onPointerLeave={(e) => {
                  if (hoverGesture.leave(e)) stage.setHovered(null);
                }}
                onClick={(e) =>
                  stage.select(s.id, {
                    kind: "step",
                    id: s.id,
                    index: i,
                    element: e.currentTarget,
                  })
                }
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                opacity={dim ? 0.35 : 1}
              >
                <polygon
                  points={points}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <foreignObject
                  x={ox + 16}
                  y={oy}
                  width={chevW - 32}
                  height={CHEV_H}
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                      ...labelTextStyle,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: textFill,
                    }}
                  >
                    {labels[i].text}
                  </div>
                </foreignObject>
                {s.detail && (
                  <foreignObject
                    x={ox - 4}
                    y={oy + CHEV_H + 4}
                    width={chevW + 8}
                    height={28}
                    overflow="visible"
                  >
                    <div
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        color: isHovered && !isActive ? "var(--muted-foreground)" : detailFill,
                        fontSize: "10px",
                        lineHeight: 1.15,
                        textAlign: "center",
                        overflowWrap: "anywhere",
                        transition: "color 0.15s",
                      }}
                    >
                      {stripInlineMarkdown(s.detail)}
                    </div>
                  </foreignObject>
                )}
              </g>
            );
          })}
        </svg>
      </SwipeScroll>

      <NodeTooltip
        visible={activeStage !== null}
        anchor={activeStage ? anchorRefs.current[activeStage.id] ?? null : null}
        contentKey={activeStage ? String(activeStage.id) : undefined}
        title={stripInlineMarkdown(activeStage?.label) || undefined}
        rows={
          activeStage
            ? [
                ...(activeStatusMeta
                  ? [{ color: activeStatusMeta.color, label: t("Status"), value: activeStatusMeta.label }]
                  : []),
                ...(activeStage.detail
                  ? [{ color: "var(--muted-foreground)", label: t("Detail"), value: activeStage.detail }]
                  : []),
              ]
            : []
        }
      />

      {showLegend && grouped && (
        <SwipeScroll>
          <Legend
            items={legendItems}
            hoveredIndex={legend.hovered}
            onHoverChange={legend.setHovered}
            selectedIndex={legendSelectedIndex}
            onSelect={legend.select}
            onLabelRef={legend.setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
              <LegendMarker variant="chevron" />
              <LegendLabel />
              <LegendValue />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
  );
}
